import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { JanusService, StateService } from '@shared/services';
import { BehaviorSubject, Subject } from 'rxjs';
import { CustomizationService } from '../../../services/customization.service';
import { distinctUntilChanged, map, pairwise, takeUntil } from 'rxjs/operators';
import { TokenService } from '@shared/services/token.service';
import { PlatformDetectorService } from '@shared/services/platform-detector/platform-detector.service';
import { RealtimeDatabaseSubscriptionsService } from '../../../services/rtdb-subscriptions.service';
import { ScreenShareRtdb } from '@models';

@Component({
  selector: 'thevatra-screen-share-control',
  templateUrl: './screen-share-control.component.html',
  styleUrls: ['./screen-share-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScreenShareControlComponent implements OnInit, OnDestroy {
  public showShareScreenButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly destroyed$ = new Subject<boolean>();

  constructor(
    public readonly stateService: StateService,
    private readonly janusService: JanusService,
    private readonly customizationService: CustomizationService,
    private readonly tokenService: TokenService,
    private readonly platformDetectorService: PlatformDetectorService,
    private readonly rtdbSubscriptionsService: RealtimeDatabaseSubscriptionsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.handleHostForShowingScreenButton();

    this.rtdbSubscriptionsService.screenShareSubscription$
      .pipe(
        map((value: { [userId: string]: ScreenShareRtdb } | null) => value && value[this.stateService.userId]),
        distinctUntilChanged(),
        pairwise(),
        takeUntil(this.destroyed$),
      )
      .subscribe(([previous, current]: [ScreenShareRtdb | null, ScreenShareRtdb | null]) => {
        if (previous?.screenShareId === this.janusService.localScreenShareId && current === null) {
          // Reset button state when screenshare stopped by outer user action
          // For example by clicking special browser button "Stop sharing"
          this.stateService.isScreenShareClicked = false;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public toggleShareScreen(): void {
    const isScreenShareEnabled = this.stateService.isScreenShareEnabled;

    if (this.platformDetectorService.isSafariDesktop()) {
      // this.popupService.dialog(ScreenShareNotAvailablePopupComponent);
      this.janusService.switchSafariShareScreen(!isScreenShareEnabled);
      this.stateService.isScreenShareEnabled = !isScreenShareEnabled;
      return;
    }

    this.janusService.switchShareScreen(!isScreenShareEnabled);
    this.stateService.isScreenShareEnabled = !isScreenShareEnabled;
    this.stateService.isScreenShareClicked = !this.stateService.isScreenShareClicked;
    this.changeDetectorRef.markForCheck();
  }

  private handleHostForShowingScreenButton(): void {
    this.tokenService.isHost$.pipe(takeUntil(this.destroyed$)).subscribe(this.showShareScreenButton$);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
